import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  Group,
  GroupManifest,
  GroupsIndex,
  StatusResponse,
  SubscribeResponse,
  User,
  UsersResponse,
} from '@thiememeulenhoff/types';
import { map } from 'rxjs';
import { GroupEndpointsService } from './group.endpoints.service';

type GroupRequestParams = {
  scope: string;
  include_user_count: boolean;
  limit: number;
  school_year_ids?: string;
  my_groups?: boolean;
};

@Service()
export class GroupService {
  readonly MAX_GROUPS_LIMIT = 500;

  readonly #http = inject(HttpClient);

  readonly #endpoints = inject(GroupEndpointsService);

  public fetchGroups(
    scope: string,
    includeUserCount = false,
    allSchoolyears = false,
    fetchOnlyMyGroups = false,
  ) {
    const endpoint = this.#endpoints.groups();
    const params: GroupRequestParams = {
      scope,
      include_user_count: includeUserCount,
      limit: this.MAX_GROUPS_LIMIT,
    };

    if (allSchoolyears) {
      params.school_year_ids = '';
    }

    if (fetchOnlyMyGroups) {
      params.my_groups = true;
    }

    return this.#http.get<GroupsIndex>(endpoint, { params });
  }

  public fetchGroup(id: string) {
    const endpoint = this.#endpoints.group(id);
    return this.#http.get<Group>(endpoint);
  }

  public fetchUsersByProfileIds(tmProfileIds: Array<string>) {
    const endpoint = this.#endpoints.users();
    return this.#http
      .get<UsersResponse>(endpoint, {
        params: {
          tm_profile_ids: tmProfileIds.join(','),
        },
      })
      .pipe(map((response) => response.items));
  }

  public fetchCurrentUser() {
    const endpoint = this.#endpoints.currentUser();
    return this.#http.get<User>(endpoint);
  }

  public fetchManifest() {
    const endpoint = this.#endpoints.manifest();
    return this.#http.get<GroupManifest>(endpoint);
  }

  public postSubscribe(userId: string, code: string) {
    const endpoint = this.#endpoints.subscribe(userId);
    return this.#http.post<SubscribeResponse>(endpoint, {
      accessCode: code,
    });
  }

  public postLinkDemo(sourceGroupId: string, code: string) {
    const endpoint = this.#endpoints.demo(sourceGroupId, code);
    return this.#http.post<Group>(endpoint, {});
  }

  public cloneDemoData(groupId: string) {
    const endpoint = this.#endpoints.cloneDemoData(groupId);
    return this.#http.get<StatusResponse>(endpoint, {});
  }
}
